/**
 * Pure utility functions for chat room name generation and duplicate detection.
 * Extracted from chat.js for testability.
 */

/**
 * Generate the display name for a chat room from the current user's perspective.
 *
 * Rules:
 * - 1-on-1 rooms (2 users): ALWAYS show the other person's name, unless the room
 *   has a custom name (one that doesn't match any participant's username).
 * - Group rooms (3+ users): Use stored roomName if it exists and is non-empty.
 *   Otherwise, generate from contacts (other users' names joined by comma).
 * - Solo rooms (1 user): Show "Myself".
 *
 * @param {Object} params
 * @param {string} params.storedRoomName - The roomName stored in Firestore
 * @param {Array}  params.roomUsers - All users in the room [{employeeID, username}, ...]
 * @param {string} params.currentUserId - The logged-in user's employeeID
 * @returns {string} The display name for this room
 */
export function generateRoomDisplayName({ storedRoomName, roomUsers, currentUserId }) {
    const contacts = roomUsers.filter(u => u.employeeID !== currentUserId);
    const contactNames = contacts.map(u => u.username).filter(Boolean);
    const allUsernames = roomUsers.map(u => u.username).filter(Boolean);

    // 1-on-1 room: always show the other person's name
    // Exception: if storedRoomName is custom (doesn't match any participant)
    if (roomUsers.length === 2) {
        if (storedRoomName && storedRoomName.trim() !== '' && !allUsernames.includes(storedRoomName.trim())) {
            // Custom name that doesn't match any participant — respect it
            return storedRoomName.trim();
        }
        // Auto-generate: show the other person's name
        return contactNames.join(', ') || 'Myself';
    }

    // Group room (3+ users): use stored name if it's non-empty, otherwise generate
    if (storedRoomName && storedRoomName.trim() !== '') {
        return storedRoomName.trim();
    }

    // Fallback: comma-separated contact names
    return contactNames.join(', ') || 'Myself';
}

/**
 * Check if a room with the exact same set of participants already exists.
 *
 * @param {Array}  existingRooms - Array of room objects with .users[].employeeID
 * @param {Array}  newUserIds - Array of employeeID strings for the new room (INCLUDING current user)
 * @returns {Object|null} The existing room if found, null otherwise
 */
export function findRoomWithSameParticipants(existingRooms, newUserIds) {
    const sortedNew = [...newUserIds].sort();
    return existingRooms.find(room => {
        const sortedExisting = room.users.map(u => u.employeeID).sort();
        if (sortedExisting.length !== sortedNew.length) return false;
        return sortedExisting.every((id, i) => id === sortedNew[i]);
    }) || null;
}
